import { inject } from '@angular/core';
import { UrlSegment } from '@angular/router';
import { catchError, first, map, Observable, of, switchMap } from 'rxjs';
import { UserService } from '@origin8-web/core-ui/login/providers';
import { AUTH_PATH } from '@origin8-web/core-ui/login/routes';
import { CLIENT_ORGANIZATIONS, SUPERVISR_ORGANIZATION } from '@origin8-web/models/core-business';
import { SUPERVISOR_VIEW_ROUTE, SUPERVISORS_ROUTE, UNAUTHORIZED_FULL_PATH } from '../../app.interface';
import { InteractionServiceGateway } from '../../service-gateways/interactions/interaction.service-gateway';
import { buildPathWithOrganization } from './organization-router.util';

/**
 * Top-level path prefixes that carry :organization as their second segment
 * (e.g. `supervisor/:organization/:supervisorId`, `rules/:organization/edit/:id`).
 * When the org is stripped from the URL we re-insert it right after the prefix so
 * the user stays on the page they were on instead of bouncing to the landing page.
 */
const ORG_SCOPED_PREFIXES = new Set<string>([
  'rules',
  'supervisors',
  'supervisor',
  'lead-lifecycle',
  'interactions-history',
  'leads',
  'interaction',
  'integrations',
]);

/** Supervisr admins have no client org of their own, so default them to the first client. */
function resolveUserOrganization(organization: string): string {
  return organization === SUPERVISR_ORGANIZATION ? CLIENT_ORGANIZATIONS[0].name : organization;
}

/**
 * Rebuilds an org-scoped path from the segments left after the :organization
 * segment was removed from the URL. Re-inserts the org after a known prefix to
 * preserve the page. Returns null for anything unrecognized — including the empty
 * path — leaving the caller to resolve the landing page, which needs a round trip.
 */
export function buildOrgScopedPath(segments: string[], organization: string): string | null {
  const [prefix, ...rest] = segments;
  if (prefix && ORG_SCOPED_PREFIXES.has(prefix) && rest[0] !== organization) {
    return ['', prefix, organization, ...rest].join('/');
  }
  return null;
}

/**
 * The app's landing page: the org's first supervisor. There is no home dashboard any more, and a
 * supervisor is what the app is actually about, so entering at `/` drops straight into one. The
 * supervisor list only exists server-side, hence the fetch; an org with no supervisors (or a failed
 * lookup) lands on the supervisors list, where the user can create one.
 */
function resolveLandingPath(organization: string, interactionGateway: InteractionServiceGateway): Observable<string> {
  const supervisorsFallback = buildPathWithOrganization(SUPERVISORS_ROUTE.BASE_FULL_PATH, organization);
  return interactionGateway.getSupervisorsForOrg(organization).pipe(
    first(),
    map((supervisors) => {
      // Prefer an active supervisor: an inactive one shows empty tabs and reads as a broken landing.
      const supervisor = supervisors.find((s) => s.isActive) ?? supervisors[0];
      return supervisor
        ? buildPathWithOrganization(SUPERVISOR_VIEW_ROUTE.BASE_FULL_PATH, organization, {
            supervisorId: supervisor.id,
          })
        : supervisorsFallback;
    }),
    catchError(() => of(supervisorsFallback)),
  );
}

/**
 * Resolves a redirect target for routes reached without a valid :organization
 * segment (the empty path, or a wildcard match after the org was deleted from
 * the URL). Reads the authenticated user's organization and rebuilds a valid
 * org-scoped URL, preserving the attempted page when possible. Logged-out users
 * go to the login page with a redirect back to where they were headed.
 *
 * Both the empty-path and wildcard routes use this so the wildcard resolves the
 * final URL directly instead of bouncing through the empty route — a chained
 * redirect into an async function redirect that Angular doesn't reliably resolve,
 * which left navigation stranded at `/` until a manual refresh.
 */
export function redirectToOrganizationScopedPath(url: UrlSegment[]): Observable<string> {
  const userService = inject(UserService);
  const interactionGateway = inject(InteractionServiceGateway);
  const segments = url.map((segment) => segment.path);
  return userService.getIsLoggedIn$().pipe(
    first(),
    switchMap((loggedIn) => {
      if (!loggedIn) {
        // encodeURI (not encodeURIComponent) to match how the login page reads it back: decodeURI(params['redirect']).
        const attempted = '/' + segments.join('/');
        return of(`${AUTH_PATH.LOGIN_FULL_PATH}?redirect=${encodeURI(attempted)}`);
      }
      return userService.getUser$().pipe(
        first(),
        switchMap((user) => {
          // Null for an authenticated user whose token carries no organization. Every path this
          // function can build is org-scoped, so there is nothing to redirect to; say so rather
          // than assert non-null and throw inside the router's redirect, which strands the app on
          // a blank page with no route at all.
          if (!user) {
            return of(UNAUTHORIZED_FULL_PATH);
          }
          const organization = resolveUserOrganization(user.organization);
          const scopedPath = buildOrgScopedPath(segments, organization);
          return scopedPath ? of(scopedPath) : resolveLandingPath(organization, interactionGateway);
        }),
      );
    }),
  );
}
